import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Col, Input, Modal, Row, Space, Switch, Tag, Typography } from 'antd'
import WhatsAppPreview from './WhatsAppPreview'
import { KIND_WHEN, serverSentence } from './labels'
import {
  patchRequest,
  postRequest,
  successNotification,
  dutyFollowUpMessageUrl,
  dutyFollowUpPreviewUrl,
  dutyFollowUpResetUrl
} from '../../../config'

const { Text, Paragraph } = Typography
const noop = () => {}

// One follow-up message: its switch, its text, and what a customer would actually receive
// (spec: duty-follow-up-messages, w-admin Req 1).
const MessageCard = ({ message, onSaved, onDirtyChange }) => {
  const [draft, setDraft] = useState(message.body)
  const [enabled, setEnabled] = useState(message.enabled)
  const [saving, setSaving] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState({ text: '', buttons: [], droppedLines: [] })
  const [previewing, setPreviewing] = useState(false)
  const textArea = useRef(null)
  const selection = useRef({ start: null, end: null })

  const dirty = draft !== message.body

  useEffect(() => {
    setDraft(message.body)
    setEnabled(message.enabled)
  }, [message.body, message.enabled])

  useEffect(() => {
    onDirtyChange(message.kind, dirty)
    // eslint-disable-next-line
  }, [dirty])

  // The preview follows the draft as the owner types, rendered by the server with the same rules
  // the customer's message goes through — debounced so it is not a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(async () => {
      const response = await postRequest(
        dutyFollowUpPreviewUrl(message.kind),
        { body: draft },
        'preview',
        setPreviewing
      )
      if (response.success) setPreview(response.data)
    }, 400)
    return () => clearTimeout(timer)
  }, [draft, message.kind])

  const rememberSelection = () => {
    const el = textArea.current?.resizableTextArea?.textArea
    if (el) selection.current = { start: el.selectionStart, end: el.selectionEnd }
  }

  const insertPlaceholder = name => {
    const token = `{${name}}`
    const { start, end } = selection.current
    const at = start === null ? draft.length : start
    const to = end === null ? draft.length : end
    setDraft(draft.slice(0, at) + token + draft.slice(to))
    selection.current = { start: at + token.length, end: at + token.length }
  }

  const toggle = async value => {
    setEnabled(value)
    const response = await patchRequest(
      dutyFollowUpMessageUrl(message.kind),
      { enabled: value },
      'message',
      setSwitching
    )
    if (!response.success) {
      setEnabled(!value) // roll back: the switch shows what is actually saved
      return
    }
    onSaved(response.data)
  }

  const save = async () => {
    setError('')
    const response = await patchRequest(
      dutyFollowUpMessageUrl(message.kind),
      { body: draft },
      'message',
      setSaving
    )
    if (!response.success) {
      setError(serverSentence(response) || 'Could not save the message.')
      return
    }
    successNotification('Message saved')
    onSaved(response.data)
  }

  const reset = () =>
    Modal.confirm({
      title: 'Reset to the default text?',
      content: 'Your edits to this message will be replaced by the text it shipped with.',
      okText: 'Reset',
      onOk: async () => {
        const response = await postRequest(
          dutyFollowUpResetUrl(message.kind),
          {},
          'message',
          noop
        )
        if (response.success) {
          setError('')
          onSaved(response.data)
        }
      }
    })

  const length = (preview.text || '').length
  const tooLong = length > message.maxLength

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          {message.label}
          {dirty && <Tag color="orange">Unsaved</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Text type="secondary">{enabled ? 'On' : 'Off'}</Text>
          <Switch checked={enabled} loading={switching} onChange={toggle} />
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ marginBottom: 4 }}>
        {KIND_WHEN[message.kind]}
      </Paragraph>
      <Paragraph type="warning" style={{ fontSize: 12 }}>
        Keep it short — most customers read only the first line.
      </Paragraph>
      <Row gutter={24}>
        <Col xs={24} lg={13}>
          <Input.TextArea
            ref={textArea}
            value={draft}
            autoSize={{ minRows: 6, maxRows: 16 }}
            onChange={e => {
              setDraft(e.target.value)
              rememberSelection()
            }}
            onSelect={rememberSelection}
            onClick={rememberSelection}
            onKeyUp={rememberSelection}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ marginRight: 8 }}>
              Insert:
            </Text>
            {message.placeholders.map(name => (
              <Tag
                key={name}
                color="blue"
                style={{ cursor: 'pointer', marginBottom: 4 }}
                onClick={() => insertPlaceholder(name)}
              >
                {`{${name}}`}
              </Tag>
            ))}
          </div>
          <div style={{ marginTop: 4 }}>
            <Text type={tooLong ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {length} / {message.maxLength} characters
              {message.hasButtons ? ' (a message with buttons)' : ''}
            </Text>
          </div>
          {error && (
            <div style={{ marginTop: 8 }}>
              <Text type="danger">{error}</Text>
            </div>
          )}
          <Space style={{ marginTop: 12 }}>
            <Button type="primary" onClick={save} loading={saving} disabled={!dirty || tooLong}>
              Save
            </Button>
            <Button onClick={() => setDraft(message.body)} disabled={!dirty}>
              Discard changes
            </Button>
            <Button type="link" onClick={reset}>
              Reset to default
            </Button>
          </Space>
        </Col>
        <Col xs={24} lg={11}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            What the customer sees
          </Text>
          <WhatsAppPreview
            text={preview.text}
            buttons={preview.buttons}
            droppedLines={preview.droppedLines}
            loading={previewing}
          />
        </Col>
      </Row>
    </Card>
  )
}

MessageCard.propTypes = {
  message: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    label: PropTypes.string,
    enabled: PropTypes.bool,
    body: PropTypes.string,
    placeholders: PropTypes.arrayOf(PropTypes.string),
    maxLength: PropTypes.number,
    hasButtons: PropTypes.bool
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func
}

MessageCard.defaultProps = {
  onDirtyChange: noop
}

export default MessageCard
